import { useState } from "react";
import { X } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { Button } from "../../primitives/button";

interface MarkInactiveDialogProps {
  personId: string;
  personName: string;
  onClose: () => void;
  /** Called after a successful mark-inactive (and, if requested, a
   *  successful replacement-contact creation) so the caller can refresh
   *  its list. */
  onDone: () => void;
}

type Step = "confirm" | "replacement" | "replacement_result";

/**
 * Marks a contact inactive (no longer a reachable contact — left the
 * company, changed role) and offers an immediate, pre-filled path to add
 * their replacement at the same company. Deliberately separate from
 * EditPersonDialog: this is an event with a consequence, not a field edit.
 */
export function MarkInactiveDialog({
  personId,
  personName,
  onClose,
  onDone,
}: MarkInactiveDialogProps) {
  const dataAdapter = useDataAdapter();
  const [step, setStep] = useState<Step>("confirm");
  const [reason, setReason] = useState("");
  const [addReplacement, setAddReplacement] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactSynced, setContactSynced] = useState(false);
  const [syncProblems, setSyncProblems] = useState<string[]>([]);
  const [syncedTo, setSyncedTo] = useState<"tharstern" | "customer">(
    "tharstern",
  );

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await dataAdapter.markMarketingPersonInactive(
        personId,
        reason.trim() || null,
      );
      if (addReplacement) {
        setStep("replacement");
      } else {
        onDone();
        onClose();
      }
    } catch (e) {
      console.error("marketing.contacts.mark_inactive.failed", e);
      setError("Couldn't mark this contact inactive. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateReplacement() {
    setSaving(true);
    setError(null);
    try {
      const result = await dataAdapter.createReplacementContact({
        originalPersonId: personId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      setContactSynced(result.contactSynced);
      setSyncProblems(result.syncProblems);
      setSyncedTo(result.syncedTo);
      setStep("replacement_result");
      onDone();
    } catch (e) {
      console.error("marketing.contacts.create_replacement.failed", e);
      setError("Couldn't create the replacement contact. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-base">
            {step === "confirm"
              ? "Mark contact inactive"
              : step === "replacement"
                ? "Add their replacement"
                : "Replacement contact added"}
          </h2>
          <button onClick={onClose} aria-label="Close">
            <X className="size-5 text-muted-foreground hover:text-foreground" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          {error && <p className="text-sm text-red-600">{error}</p>}

          {step === "confirm" && (
            <>
              <p className="text-sm text-muted-foreground">
                {personName} will no longer be treated as a reachable contact
                and will be excluded from campaigns. Other contacts at their
                company are unaffected.
              </p>
              <label className="block text-xs text-muted-foreground">
                Reason (optional)
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. left the company, replaced by Dave"
                  rows={2}
                  className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={addReplacement}
                  onChange={(e) => setAddReplacement(e.target.checked)}
                />
                Add their replacement now?
              </label>
            </>
          )}

          {step === "replacement" && (
            <>
              <p className="text-sm text-muted-foreground">
                Carrying over the same company, account, and sales rep.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs text-muted-foreground">
                  First name
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
                  />
                </label>
                <label className="block text-xs text-muted-foreground">
                  Last name
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
                  />
                </label>
              </div>
              <label className="block text-xs text-muted-foreground">
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
                />
              </label>
              <label className="block text-xs text-muted-foreground">
                Phone
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
                />
              </label>
            </>
          )}

          {step === "replacement_result" && (
            <>
              <p className="text-sm text-foreground">
                {firstName} {lastName} added as a contact.
              </p>
              {contactSynced ? (
                <p className="text-sm text-green-700">
                  {syncedTo === "customer"
                    ? "Added to the customer's people."
                    : "Synced to Tharstern."}
                </p>
              ) : (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                  <p className="font-medium">
                    {syncedTo === "customer"
                      ? "Not added to the customer's people."
                      : "Not synced to Tharstern yet."}
                  </p>
                  {syncProblems.map((p, i) => (
                    <p key={i} className="text-amber-700">
                      {p}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-border">
          {step === "replacement_result" ? (
            <Button size="sm" onClick={onClose}>
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={
                  step === "confirm" ? handleConfirm : handleCreateReplacement
                }
                disabled={
                  saving ||
                  (step === "replacement" &&
                    (!firstName.trim() || !lastName.trim()))
                }
              >
                {saving
                  ? "Saving…"
                  : step === "confirm"
                    ? "Mark inactive"
                    : "Add contact"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
